import React, { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Package, Truck, MapPin, ShoppingBag, ChevronRight, Mail } from 'lucide-react';

const OrderSuccess = () => {
  const { state } = useLocation();
  const navigate  = useNavigate();
  const order     = state?.order;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    document.title = 'Order Confirmed | Aamira';
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  if (!order) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: 162, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Jost, sans-serif', background: '#FAF8F4' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#9A9186', fontSize: 13, marginBottom: 20 }}>No order found.</p>
          <Link to="/collections" style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#B08D57', textDecoration: 'none' }}>
            Back to Collections →
          </Link>
        </div>
      </div>
    );
  }

  const items   = Array.isArray(order.items) ? order.items : [];
  const total   = Number(order.total || 0);
  const savings = order.voucher_discount ? Number(order.voucher_discount) : 0;

  const STEPS = [
    { icon: CheckCircle, label: 'Order Confirmed',  sub: 'Payment received'     },
    { icon: Package,     label: 'Being Packed',      sub: '1–2 business days'   },
    { icon: Truck,       label: 'Out for Delivery',  sub: '2–7 business days'   },
    { icon: MapPin,      label: 'Delivered',          sub: 'To your address'     },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

        .os-page {
          background: #FAF8F4;
          min-height: 100vh;
          padding-top: 162px;
          padding-bottom: 100px;
          font-family: 'Jost', sans-serif;
          position: relative;
          overflow-x: hidden;
        }
        .os-page::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.022'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 0;
        }

        .os-inner {
          position: relative;
          z-index: 1;
          max-width: 780px;
          margin: 0 auto;
          padding: 0 clamp(20px, 5vw, 48px);
        }

        /* ── Hero ── */
        .os-hero {
          text-align: center;
          padding: 48px 0 40px;
          border-bottom: 1px solid rgba(176,141,87,0.15);
          margin-bottom: 40px;
        }
        .os-icon-wrap {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: rgba(176,141,87,0.1);
          border: 1.5px solid rgba(176,141,87,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }
        .os-eyebrow {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
        }
        .os-eyebrow::before,
        .os-eyebrow::after {
          content: '';
          width: 28px;
          height: 1px;
          background: rgba(176,141,87,0.5);
        }
        .os-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(28px, 5vw, 42px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 1.08;
          letter-spacing: -0.02em;
          margin-bottom: 10px;
        }
        .os-title em {
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
        }
        .os-sub {
          font-family: 'Cormorant Garamond', serif;
          font-size: 17px;
          font-style: italic;
          font-weight: 300;
          color: #8A7E72;
          letter-spacing: 0.03em;
        }
        .os-order-num {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-top: 18px;
          background: #fff;
          border: 1px solid rgba(176,141,87,0.2);
          padding: 8px 18px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          color: #5C5247;
        }
        .os-order-num span { color: #B08D57; }

        /* ── Progress ── */
        .os-progress {
          background: #fff;
          border: 1px solid rgba(176,141,87,0.12);
          padding: 28px 24px;
          margin-bottom: 20px;
        }
        .os-progress-label {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .os-progress-label::before {
          content: '';
          width: 16px;
          height: 1px;
          background: #B08D57;
        }
        .os-steps {
          display: flex;
          align-items: flex-start;
          gap: 0;
        }
        .os-step {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          position: relative;
        }
        .os-step:not(:last-child)::after {
          content: '';
          position: absolute;
          top: 18px;
          left: 50%;
          width: 100%;
          height: 1px;
          background: rgba(176,141,87,0.2);
          z-index: 0;
        }
        .os-step-dot {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 1;
          margin-bottom: 10px;
          flex-shrink: 0;
        }
        .os-step-dot--active {
          background: #B08D57;
          border: 1.5px solid #B08D57;
        }
        .os-step-dot--inactive {
          background: #fff;
          border: 1.5px solid rgba(176,141,87,0.25);
        }
        .os-step-name {
          font-size: 10px;
          font-weight: 600;
          color: #1C1A18;
          letter-spacing: 0.04em;
          margin-bottom: 3px;
        }
        .os-step-sub {
          font-size: 9px;
          color: #9A9186;
          letter-spacing: 0.04em;
        }

        /* ── Summary cards ── */
        .os-cards {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 20px;
        }
        .os-card {
          background: #fff;
          border: 1px solid rgba(176,141,87,0.12);
          padding: 20px;
        }
        .os-card-label {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .os-card-label::before {
          content: '';
          width: 12px;
          height: 1px;
          background: #B08D57;
        }

        /* Order items */
        .os-item-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 0;
          border-bottom: 1px solid rgba(176,141,87,0.07);
        }
        .os-item-row:last-child { border-bottom: none; }
        .os-item-img {
          width: 44px;
          height: 44px;
          background: #F0EAE0;
          overflow: hidden;
          flex-shrink: 0;
          border: 1px solid rgba(176,141,87,0.1);
        }
        .os-item-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .os-item-name {
          font-size: 12px;
          font-weight: 500;
          color: #1C1A18;
          flex: 1;
          line-height: 1.3;
        }
        .os-item-qty {
          font-size: 10px;
          color: #9A9186;
        }
        .os-item-price {
          font-family: 'Playfair Display', serif;
          font-size: 13px;
          font-weight: 700;
          color: #B08D57;
          white-space: nowrap;
        }

        /* Price summary */
        .os-price-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 7px 0;
          font-size: 12px;
          color: #5C5247;
          border-bottom: 1px solid rgba(176,141,87,0.07);
        }
        .os-price-row:last-child { border-bottom: none; }
        .os-price-row--total {
          padding-top: 12px;
          margin-top: 4px;
          border-top: 1px solid rgba(176,141,87,0.15);
        }
        .os-price-row--total span:first-child {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #1C1A18;
        }
        .os-price-row--total span:last-child {
          font-family: 'Playfair Display', serif;
          font-size: 18px;
          font-weight: 700;
          color: #B08D57;
        }
        .os-savings-badge {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #2E7D32;
        }

        /* ── Email note ── */
        .os-email-note {
          background: rgba(176,141,87,0.07);
          border: 1px solid rgba(176,141,87,0.2);
          padding: 14px 18px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 28px;
          font-size: 12px;
          color: #5C5247;
          line-height: 1.6;
        }

        /* ── Free gift ── */
        .os-gift {
          background: linear-gradient(135deg, #1C1A18 0%, #2C2A26 100%);
          border: 1px solid rgba(176,141,87,0.3);
          padding: 18px 22px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .os-gift-icon {
          font-size: 28px;
          flex-shrink: 0;
        }
        .os-gift-text-title {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 3px;
        }
        .os-gift-text-sub {
          font-size: 12px;
          color: rgba(255,255,255,0.65);
          font-weight: 300;
        }

        /* ── CTA row ── */
        .os-cta {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .os-btn-primary {
          flex: 1;
          background: #1C1A18;
          color: #FAF8F4;
          border: none;
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          padding: 15px 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          text-decoration: none;
          position: relative;
          overflow: hidden;
          transition: color 0.28s;
          white-space: nowrap;
        }
        .os-btn-primary::after {
          content: '';
          position: absolute;
          inset: 0;
          background: #B08D57;
          transform: translateX(-101%);
          transition: transform 0.28s cubic-bezier(0.4,0,0.2,1);
          z-index: 0;
        }
        .os-btn-primary:hover::after { transform: translateX(0); }
        .os-btn-primary > * { position: relative; z-index: 1; }

        .os-btn-outline {
          flex: 1;
          background: transparent;
          color: #5C5247;
          border: 1px solid rgba(176,141,87,0.25);
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          padding: 15px 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          text-decoration: none;
          transition: border-color 0.2s, color 0.2s;
          white-space: nowrap;
        }
        .os-btn-outline:hover { border-color: #B08D57; color: #B08D57; }

        /* ── Ornament ── */
        .os-ornament {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin: 40px 0 0;
          opacity: 0.3;
        }
        .os-ornament::before,
        .os-ornament::after {
          content: '';
          width: 60px;
          height: 1px;
          background: #B08D57;
        }
        .os-diamond {
          width: 6px;
          height: 6px;
          background: #B08D57;
          transform: rotate(45deg);
        }

        @media (max-width: 600px) {
          .os-page { padding-top: 110px; }
          .os-cards { grid-template-columns: 1fr; }
          .os-steps { gap: 0; }
          .os-step-name { font-size: 9px; }
          .os-cta { flex-direction: column; }
        }
      `}</style>

      <div className="os-page">
        <div className="os-inner">

          {/* ── Hero ── */}
          <motion.div
            className="os-hero"
            initial={{ opacity: 0, y: 20 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <motion.div
              className="os-icon-wrap"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={visible ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.15, type: 'spring', stiffness: 300 }}
            >
              <CheckCircle size={32} color="#B08D57" strokeWidth={1.5} />
            </motion.div>

            <p className="os-eyebrow">Order Confirmed</p>
            <h1 className="os-title">Thank You for Your <em>Order</em></h1>
            <p className="os-sub">Your jewellery is being prepared with care.</p>

            {order.order_number && (
              <div className="os-order-num">
                Order <span>#{order.order_number}</span>
              </div>
            )}
          </motion.div>

          {/* ── Free gift banner ── */}
          {order.free_gift && (
            <motion.div
              className="os-gift"
              initial={{ opacity: 0, y: 10 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              <span className="os-gift-icon">🎁</span>
              <div>
                <p className="os-gift-text-title">Free Gift Included!</p>
                <p className="os-gift-text-sub">A complimentary gift has been added to your order. Enjoy!</p>
              </div>
            </motion.div>
          )}

          {/* ── Progress tracker ── */}
          <motion.div
            className="os-progress"
            initial={{ opacity: 0, y: 12 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <p className="os-progress-label">Order Status</p>
            <div className="os-steps">
              {STEPS.map((step, i) => (
                <div key={i} className="os-step">
                  <div className={`os-step-dot ${i === 0 ? 'os-step-dot--active' : 'os-step-dot--inactive'}`}>
                    <step.icon
                      size={16}
                      color={i === 0 ? '#fff' : 'rgba(176,141,87,0.4)'}
                      strokeWidth={1.8}
                    />
                  </div>
                  <p className="os-step-name">{step.label}</p>
                  <p className="os-step-sub">{step.sub}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── Summary cards ── */}
          <motion.div
            className="os-cards"
            initial={{ opacity: 0, y: 12 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {/* Items */}
            <div className="os-card">
              <p className="os-card-label">Items Ordered</p>
              {items.length === 0 ? (
                <p style={{ fontSize: 12, color: '#9A9186' }}>No items found.</p>
              ) : (
                items.map((item, i) => (
                  <div key={i} className="os-item-row">
                    <div className="os-item-img">
                      {item.image && <img src={item.image} alt={item.name} />}
                    </div>
                    <div className="os-item-name">{item.name}</div>
                    <span className="os-item-qty">×{item.qty}</span>
                    <span className="os-item-price">
                      ₹{Number(item.price * item.qty).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* Price summary */}
            <div className="os-card">
              <p className="os-card-label">Price Breakdown</p>

              <div className="os-price-row">
                <span>Subtotal</span>
                <span>₹{Number(total + savings - (order.delivery_charge || 0)).toLocaleString('en-IN')}</span>
              </div>

              {savings > 0 && (
                <div className="os-price-row">
                  <span>Voucher Discount</span>
                  <span className="os-savings-badge">−₹{Number(savings).toLocaleString('en-IN')}</span>
                </div>
              )}

              <div className="os-price-row">
                <span>Delivery</span>
                <span style={{ color: order.delivery_charge > 0 ? '#1C1A18' : '#2E7D32', fontSize: 11 }}>
                  {order.delivery_charge > 0
                    ? `₹${Number(order.delivery_charge).toLocaleString('en-IN')}`
                    : 'Free'}
                </span>
              </div>

              {order.payment_method && (
                <div className="os-price-row">
                  <span>Payment</span>
                  <span style={{ textTransform: 'uppercase', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', color: '#B08D57' }}>
                    {order.payment_method}
                  </span>
                </div>
              )}

              <div className="os-price-row os-price-row--total">
                <span>Total Paid</span>
                <span>₹{total.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </motion.div>

          {/* ── Email note ── */}
          <motion.div
            className="os-email-note"
            initial={{ opacity: 0 }}
            animate={visible ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Mail size={16} color="#B08D57" strokeWidth={1.5} style={{ flexShrink: 0 }} />
            <span>
              A confirmation email has been sent to your registered email address.
              You'll receive tracking details once your order is dispatched.
            </span>
          </motion.div>

          {/* ── CTAs ── */}
          <motion.div
            className="os-cta"
            initial={{ opacity: 0, y: 8 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.55 }}
          >
            <Link to="/collections" className="os-btn-primary">
              <ShoppingBag size={13} />
              <span>Continue Shopping</span>
              <ChevronRight size={13} />
            </Link>
            <Link to="/account" className="os-btn-outline">
              <Package size={13} />
              <span>View My Orders</span>
            </Link>
          </motion.div>

          {/* ── Ornament ── */}
          <div className="os-ornament">
            <div className="os-diamond" />
          </div>

        </div>
      </div>
    </>
  );
};

export default OrderSuccess;